import { mobile } from '@bilibili-maoer/web-utils'

import { PropsWithChildren, useMemo, useReducer } from 'react'

import {
  Box,
  PaletteMode,
  ThemeProvider,
  colors,
  createTheme,
  formLabelClasses,
  inputBaseClasses,
  textFieldClasses,
} from '@mui/material'
import { zhCN } from '@mui/material/locale'
import { zhCN as datePickerLocaleZhCN, pickersDayClasses } from '@mui/x-date-pickers'

import type {} from '@mui/x-date-pickers/themeAugmentation'
import { ThemeModeToggle } from '~/shared/components/theme-mode-toggle'

interface PerMode {
  <T extends unknown[]>(...values: T): T[number]
}

declare module '@mui/material' {
  interface SelectClasses {
    root: string
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    perMode: PerMode
  }
  interface ThemeOptions {
    perMode: PerMode
  }
}

const browserMode = () => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')

const appMode = () => (mobile.getAppInfo()?.theme === 'Dark' ? 'dark' : 'light')

const Theme = (props: PropsWithChildren<{}>) => {
  const [mode, toggleMode] = useReducer(
    (state: PaletteMode) => {
      if (process.env.NODE_ENV === 'development') {
        return state === 'light' ? 'dark' : 'light'
      }
      return state
    },
    process.env.NODE_ENV === 'development' ? browserMode() : appMode()
  )
  const theme = useMemo(() => {
    const perMode: PerMode = (...values) => (mode === 'light' ? values[0] : values[1])
    const theme = createTheme(
      {
        /* eslint-disable @typescript-eslint/naming-convention */
        perMode,
        palette: {
          mode,
          primary: {
            main: perMode('hsl(10, 80%, 65%)', 'hsl(10, 46%, 44%)'),
            contrastText: perMode('hsl(0, 0%, 100%)', 'hsl(0, 0%, 16%)'),
            light: colors.grey[400] /** #bdbdbd */,
          },
          /** 比如侧边栏的颜色（理论上不需要区分亮暗色） */
          secondary: {
            main: perMode('#3d3d3d', '#3d3d3d') /** 深灰色 */,
          },
          error: {
            main: '#cd3333',
          },
          text: {
            primary: perMode('hsl(0, 0%, 24%)', 'hsl(0, 0%, 74%)'),
            secondary: 'hsl(0, 0%, 46%)',
          },
          background: {
            default: perMode('hsl(0, 0%, 100%)', 'hsl(0, 0%, 16%)'),
            paper: perMode('hsl(0, 0%, 98%)', 'hsl(0, 0%, 14%)'),
          },
        },
        shape: {
          borderRadius: 6,
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: ({ theme }) => ({
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
              }),
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                marginLeft: '1rem',
                marginRight: '1rem',
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: ({ theme }) => ({
                color: perMode('hsl(0, 0%, 24%)', 'hsl(0, 0%, 74%)'),
                [`.${textFieldClasses.root} &`]: {
                  [`&.${inputBaseClasses.disabled}`]: {
                    backgroundColor: theme.palette.background.paper,
                  },
                },
              }),
              input: {
                [`.${textFieldClasses.root} &`]: {
                  padding: '1rem',
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              notchedOutline: {
                borderColor: perMode('hsl(0, 0%, 90%)', 'hsl(0, 0%, 20%)'),
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: ({ theme }) => ({
                cursor: 'pointer',
                color: theme.palette.text.primary,
                textDecorationColor: theme.palette.text.primary,
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }),
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                [`.${formLabelClasses.root} + &`]: {
                  marginTop: '1rem',
                },
              },
              select: {
                padding: '1rem',
              },
              icon: {
                color: perMode('hsl(0, 0%, 74%)', 'hsl(0, 0%, 46%)'),
              },
            },
          },
          MuiAlert: {
            styleOverrides: {
              standardWarning: ({ theme }) =>
                theme.unstable_sx({
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                }),
              icon: {
                alignItems: 'center',
              },
              action: {
                alignItems: 'center',
                paddingTop: 0,
                flexShrink: 0,
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: ({ theme }) => ({
                fontSize: '1.3rem',
                color: theme.palette.text.primary,
              }),
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                [`.${formLabelClasses.root} + &`]: {
                  marginTop: '1rem',
                },
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: {
                padding: '0 2.4rem 2rem',
                justifyContent: 'center',
                '> :not(:first-of-type)': {
                  marginLeft: '1.5rem',
                },
              },
            },
          },
          MuiPickersDay: {
            styleOverrides: {
              root: {
                [`&.${pickersDayClasses.selected}`]: {
                  backgroundColor: '#f45b41',
                  [`&:focus`]: {
                    backgroundColor: '#ff7861',
                  },
                },
              },
            },
          },
        },
        typography: {
          fontFamily: '"PingFang SC", "Hiragino Sans GB", "Microsoft Yahei", "Helvetica Neue", Sans-serif, Verdana',
          htmlFontSize: 10,
          body1: {
            fontSize: '1.4rem',
          },
          body2: {
            fontSize: '1.2rem',
          },
        },
        /* eslint-enable @typescript-eslint/naming-convention */
      },
      zhCN,
      // 请根据 mui 的组件文档配置本地化，一般而言只有 Mui X 的组件需要额外逐一配置。Mui X 组件列表和概览文档：https://mui.com/x/introduction/ 。
      // 比如 Date Picker（日期选择器）组件参考 https://mui.com/x/react-data-grid/localization/ 文档进行本地化配置。
      datePickerLocaleZhCN
    )
    const { zIndex } = theme
    ;[zIndex.appBar, zIndex.drawer] = [zIndex.drawer, zIndex.appBar]
    return theme
  }, [mode])

  return (
    <ThemeProvider theme={theme}>
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ position: 'fixed', bottom: 0, right: 0 }}>
          <ThemeModeToggle mode={mode} onChange={toggleMode} />
        </Box>
      )}
      {props.children}
    </ThemeProvider>
  )
}

export default Theme
